import type { ReactNode } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { usePersistedState } from '../hooks/usePersistetState';

export type CollapsibleCardProps = {
  /** Unique key for localStorage (collapsed vs expanded). */
  storageKey: string;
  title: string;
  /** Shown on the right side of the header when collapsed (e.g. a total). */
  summary?: ReactNode;
  defaultExpanded?: boolean;
  children: ReactNode;
};

export function CollapsibleCard({
  storageKey,
  title,
  summary,
  defaultExpanded = false,
  children,
}: CollapsibleCardProps) {
  const [expanded, setExpanded] = usePersistedState(storageKey, defaultExpanded);

  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800/80">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={
          'flex w-full items-center justify-between gap-2 px-2.5 py-1.5 text-left text-[11px] font-semibold uppercase tracking-wide text-gray-500 hover:bg-gray-100/80 dark:text-zinc-400 dark:hover:bg-zinc-700/50' +
          (expanded ? ' border-b border-gray-200 dark:border-zinc-600' : '')
        }
        aria-expanded={expanded}
      >
        <span className="flex min-w-0 items-center gap-1.5">
          {expanded ? (
            <ChevronDownIcon className="size-3.5 shrink-0 text-gray-500 dark:text-zinc-500" aria-hidden />
          ) : (
            <ChevronRightIcon className="size-3.5 shrink-0 text-gray-500 dark:text-zinc-500" aria-hidden />
          )}
          <span>{title}</span>
        </span>
        {summary != null && (
          <span className="shrink-0 tabular-nums text-[11px] font-bold normal-case text-violet-700 dark:text-violet-300">
            {summary}
          </span>
        )}
      </button>

      {expanded && <div className="space-y-1.5 px-2.5 py-1.5">{children}</div>}
    </div>
  );
}
